import { ColumnState } from 'ag-grid-community';
import { CompanyDto } from 'src/dtos/company/companyDto';
import { UserResponseDto } from 'src/dtos/user/response/userResponseDto';
import { create } from 'zustand';
import { defaultPRD3020_R01Dto, PRD3020_R01DetailDto, PRD3020_R01Dto } from './Dto_PRD3020_R01';
import { DateRange } from 'react-day-picker';
import { showWarning } from 'src/utils/toast/toastUtil';
import { getFutureWeekdayExceptTodayAndTomorrow } from 'src/utils/date/dateUtils';

export interface PRD3020_R01Filter {
	compCd: string;
	dateRange: DateRange | undefined;
	workplaceCd: string;
	jobTeam: string;
	closeTag: string;
	orderEmp: string;
	orderEmpNm: string;
}

export interface Store_PRD3020_R01 {
	PRD3020_R01Filter: PRD3020_R01Filter;
	PRD3020_R01PastFilter: PRD3020_R01Filter;
	setPRD3020_R01Filter: (filter: Partial<PRD3020_R01Filter>) => void;
	setPRD3020_R01PastFilter: (filter: Partial<PRD3020_R01Filter>) => void;

    PRD3020_R01List: PRD3020_R01Dto[];
    setPRD3020_R01List: (userList: PRD3020_R01Dto[]) => void;
    insertPRD3020_R01List: (data: PRD3020_R01Dto[], index?: number) => void;
    removePRD3020_R01List: (index: number) => void;
    updatePRD3020_R01List: (index: number, data: PRD3020_R01Dto) => void;

	PRD3020_R01DetailList: PRD3020_R01DetailDto[];
	setPRD3020_R01DetailList: (list: PRD3020_R01DetailDto[]) => void;
	
	editPRD3020_R01: PRD3020_R01Dto | null;
	setEditPRD3020_R01: (editHeader: PRD3020_R01Dto | null) => void;
	

    deletedPRD3020_R01List: string[];
    addDeletedPRD3020_R01List: (idList: string[]) => void;
    removeDeletedPRD3020_R01List: (idList?: string[]) => void;

	selectedPRD3020_R01: PRD3020_R01Dto | undefined;
	setSelectedPRD3020_R01: (data: PRD3020_R01Dto | undefined) => void;
	initSelectedPRD3020_R01: () => void;

	originalPRD3020_R01List: PRD3020_R01Dto[];
	setOriginalPRD3020_R01List: (list: PRD3020_R01Dto[]) => void;

    columnState: ColumnState[] | null;
    setColumnState: (state: ColumnState[] | null) => void;
	detailColumnState: ColumnState[] | null;
	setDetailColumnState: (state: ColumnState[] | null) => void;

    scrollTop: number | null;
    setScrollTop: (top: number | null) => void;
	detailScrollTop: number | null;
	setDetailScrollTop: (top: number | null) => void;

	allCount: number;					setAllCount: (allCount: number) => void;
	modalVisible: boolean;				setModalVisible: (modalVisible: boolean) => void;
	
	isDisabled: (data: PRD3020_R01Dto) => boolean;

    initData: () => void;


}

export const defaultPRD3020_R01Filter: PRD3020_R01Filter = {
	compCd: '',
	dateRange: {
		from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
		to: getFutureWeekdayExceptTodayAndTomorrow()
	},
	workplaceCd: '',
	jobTeam: '',
	closeTag: 'N',
	orderEmp: '',
	orderEmpNm: '',
}

export const usePRD3020_R01Store = create<Store_PRD3020_R01>((set) => ({
	PRD3020_R01Filter: defaultPRD3020_R01Filter,
	PRD3020_R01PastFilter: defaultPRD3020_R01Filter,
	setPRD3020_R01PastFilter: (filter: Partial<PRD3020_R01Filter>) => set((state) => ({
		...state,
		PRD3020_R01PastFilter: { ...state.PRD3020_R01PastFilter, ...filter }
	})),
	setPRD3020_R01Filter: (filter: Partial<PRD3020_R01Filter>) => set((state) => ({
		...state,
		PRD3020_R01Filter: { ...state.PRD3020_R01Filter, ...filter }
	})),

    PRD3020_R01List: [],
    setPRD3020_R01List: (PRD3020_R01List: PRD3020_R01Dto[]) => set((state) => {
		let newList: PRD3020_R01Dto[] = [];
		let valueAndOrderIndex = 1;
		PRD3020_R01List.forEach((item, index) => {
			// console.log(index + '번째 행의 item.Index 정보: ' 
			// 	+ ' valueIndex : ' + item.valueIndex 
			// 	+ ' valueAndOrderIndex : ' + valueAndOrderIndex
			// 	+ ' orderIndex : ' + item.orderIndex 
			// 	+ ' orderIndexSeq : ' + item.orderIndexSeq);
			newList.push({ 
				...defaultPRD3020_R01Dto,
				...item,
				valueIndex: item.valueIndex > 0 ? item.valueIndex : valueAndOrderIndex,
				orderIndex: item.orderIndex > -1 ? item.orderIndex : valueAndOrderIndex,
				orderIndexSeq: item.orderIndexSeq > 0 ? item.orderIndexSeq : 0,
			});
			valueAndOrderIndex++;
		});
		return {
			...state,
			PRD3020_R01List: newList
		}
	}),
	PRD3020_R01DetailList: [],
	setPRD3020_R01DetailList: (PRD3020_R01DetailList: PRD3020_R01DetailDto[]) => set({
		PRD3020_R01DetailList,
	}),
	insertPRD3020_R01List: (data: PRD3020_R01Dto[], index?: number) => set((state) => {
		let newPRD3020_R01List = [...state.PRD3020_R01List];

		let targetRowData = newPRD3020_R01List.find(item => item.valueIndex === index);
		let targetOrderIndex = targetRowData?.orderIndex || 0;
		// 데이터에 orderIndex가 있으면 그것을 사용하고, 없으면 1 (index 파라미터는 배열 위치로만 사용)		

		const newOrderIndexSeq = (targetRowData?.orderIndexSeq || 0) + 1;
		// console.log('targetOrderIndex : ', targetOrderIndex);
		// console.log('newOrderIndexSeq : ', newOrderIndexSeq);
		let maxValueIndex = Math.max(0, ...newPRD3020_R01List.map(item => item.valueIndex || 0)) + 1;

		// newSYS4100_R01List에서 targetOrderIndex인 행들을 빼내서 newOrderIndexSeq 기준으로 다시 정렬
		// newOrderIndexSeq 보다 orderIndexSeq가 작은 행들은 유지. 
		// newOrderIndexSeq 보다 orderIndexSeq가 큰 행들은 기존 orderIndexSeq 값에 1을 더해서 저장.
		const targetRows = newPRD3020_R01List.filter(item => item.orderIndex === targetOrderIndex);
		targetRows.forEach(item => {
			if(item.orderIndexSeq < newOrderIndexSeq) {
				item.orderIndexSeq = item.orderIndexSeq;
			} else if(item.orderIndexSeq >= newOrderIndexSeq) {
				item.orderIndexSeq = item.orderIndexSeq + 1;
			}
		});

		// targetRows를 newSYS4100_R01List에 대체
		newPRD3020_R01List = newPRD3020_R01List.filter(item => item.orderIndex !== targetOrderIndex);
		newPRD3020_R01List.push(...targetRows);
		
		//새 행을 생성해서 newOrderIndexSeq 값의 행을 저장
		const newData = data.map(item => ({ ...defaultPRD3020_R01Dto, ...item, 
			valueIndex: maxValueIndex++,
			orderIndex: targetOrderIndex,
			orderIndexSeq: newOrderIndexSeq
		}));
		if (index !== undefined) {
			newPRD3020_R01List.splice(index, 0, ...newData);
		} else {
			newPRD3020_R01List.push(...newData);
		}		

		return {
			...state,
			PRD3020_R01List: newPRD3020_R01List
		}
	}),
	removePRD3020_R01List: (index: number) => set((state) => {
		const newPRD3020_R01List = [...state.PRD3020_R01List].filter((item) => item.valueIndex !== index);
		return {
			...state,
			PRD3020_R01List: newPRD3020_R01List
		}
	}),
	updatePRD3020_R01List: (valueIndex: number, data: PRD3020_R01Dto) => set((state) => {
		const _PRD3020_R01List = state.PRD3020_R01List.map(item =>
			item.valueIndex === valueIndex ? data : item
		);
		if(data.orderQty < 0) {
			showWarning('수량은 0보다 작을 수 없습니다.');
			data.orderQty = 0;
		}
		return {
			...state,
			PRD3020_R01List: _PRD3020_R01List
		}
	}),

	editPRD3020_R01: defaultPRD3020_R01Dto,
	setEditPRD3020_R01: (editHeader: PRD3020_R01Dto | null) => set((state) => ({
		...state,
		editPRD3020_R01: editHeader
	})),

    deletedPRD3020_R01List: [],
    addDeletedPRD3020_R01List: (idList: string[]) => set((state) => {
        const _deletedUserList = [...state.deletedPRD3020_R01List];
		
		for(const valueIndex of idList) {
			const isInserted = state.PRD3020_R01List.find(item => item.valueIndex === Number(valueIndex) && item.inserted);
			const isModified = state.PRD3020_R01List.find(item => item.valueIndex === Number(valueIndex) && item.isModified);
			if(!isInserted && !isModified) {
				_deletedUserList.push(valueIndex);
			}
		}

        return {
            ...state,
            deletedPRD3020_R01List: _deletedUserList
        }
    }),
    removeDeletedPRD3020_R01List: (idList?: string[]) => set((state) => ({
        ...state,
        deletedPRD3020_R01List: [...state.deletedPRD3020_R01List].filter(value => idList ? !idList.includes(value) : false)
    })),

	selectedPRD3020_R01: undefined,
	setSelectedPRD3020_R01: (data: PRD3020_R01Dto | undefined) => set({ selectedPRD3020_R01: data }),
	initSelectedPRD3020_R01: () => set({ selectedPRD3020_R01: undefined }),

	originalPRD3020_R01List: [],
	
    setOriginalPRD3020_R01List: (PRD3020_R01List: PRD3020_R01Dto[]) => set((state) => {
		let newList: PRD3020_R01Dto[] = [];
		let valueAndOrderIndex = 1;
		PRD3020_R01List.forEach((item, index) => {
			newList.push({ 
				...defaultPRD3020_R01Dto,
				...item,
				valueIndex: valueAndOrderIndex,
				orderIndex: valueAndOrderIndex,
				orderIndexSeq: valueAndOrderIndex
			});
			valueAndOrderIndex++;
		});
		return {
			...state,
			originalPRD3020_R01List: newList
		}
	}),

    columnState: null,
    setColumnState: (state: ColumnState[] | null) => set({ columnState: state }),
	detailColumnState: null,
	setDetailColumnState: (state: ColumnState[] | null) => set({ detailColumnState: state }),

    scrollTop: null,
    setScrollTop: (top: number | null) => set({ scrollTop: top }),
	detailScrollTop: null,
	setDetailScrollTop: (top: number | null) => set({ detailScrollTop: top }),

	allCount: 0,				setAllCount: (allCount: number) => set({ allCount }),
	modalVisible: false,		setModalVisible: (modalVisible: boolean) => set({ modalVisible }),
					
	isDisabled: (data: PRD3020_R01Dto) => data.acceptQty > 0,

    initData: () => set((state) => ({
        ...state,
		PRD3020_R01Filter: defaultPRD3020_R01Filter,
		PRD3020_R01PastFilter: defaultPRD3020_R01Filter,
        PRD3020_R01List: [],
		PRD3020_R01DetailList: [],
		selectedPRD3020_R01: undefined,
        deletedPRD3020_R01List: [],
		originalPRD3020_R01List: [],
		allCount: 0,
        scrollTop: null,
		detailScrollTop: null,
    }))
}));
